import React, { Component } from 'react';
import axios from 'axios';
import { Typography, withStyles, CircularProgress } from '@material-ui/core';
import { red } from '@material-ui/core/colors';
import { Auth } from 'aws-amplify';
import { Redirect } from 'react-router-dom';
import ConfirmRenewal from '../components/ConfirmRenewal';
import ConfirmDirectDebitRenewal from '../components/ConfirmDirectDebitRenewal';
import { hasValidZonePricing } from '../helpers/ticket';

const styles = theme => ({
    root: {
        border: '0',
        maxWidth: '800px',
        margin: '40px auto',
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    error: {
        margin: '20px 0 0 5px',
        color: red[500],
        minHeight: '20px',
        textAlign: 'center'
    },
    spinner: {
        marginLeft: 'calc(50% - 22px)',
        marginTop: '60px'
    },
    availabilityWarning: {
        background: 'red',
        textAlign: 'center',
        marginBottom: 20,
        padding: 5
    },
    availabilityWarningHeading: {
        color: 'white',
        fontWeight: 'bold'
    },
    availabilityWarningText: {
        color: 'white'
    }
});

class RenewTicket extends Component {
    constructor(props) {
        super(props);

        this.state = {
            error: '',
            passenger: {
                firstName: '',
                lastName: '',
                photo: null,
                zone: '',
                zoneStop: ''
            },
            stop: '',
            dest: '',
            yr: '',
            ticket: null,
            client: null,
            service: null,
            renewal: null,
            mandates: null,
            isLoading: true,
            extId: ''
        };
    }

    async componentDidMount() {
        const {
            match: {
                params: { renewalId }
            }
        } = this.props;

        try {
            // get detailed renewal
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const response = await axios.get(
                `${process.env.REACT_APP_RENEWAL_API_ENDPOINT}/renewal/detailed/${renewalId}`,
                {
                    headers
                }
            );

            // set client, service, ticket, previous customer ticket
            const { client, renewal } = response.data;
            const { passenger } = response.data.previous_customer_ticket;

            const renewalTicket = response.data.renewal_ticket;

            const ticketResponse = await axios.get(
                `${process.env.REACT_APP_TICKET_API_ENDPOINT}/ticket/${renewalTicket.id}/detailed`,
                { headers }
            );

            if (!ticketResponse.data) {
                throw new Error();
            }

            const ticket = ticketResponse.data;

            const extId = response.data.previous_customer_ticket.ext_id || '';
            const stop = response.data.previous_customer_ticket.stop || '';
            const yr = response.data.previous_customer_ticket.yr || '';
            const dest = response.data.previous_customer_ticket.dest || '';
            const zone = response.data.previous_customer_ticket.zone || '';
            const zoneStop =
                response.data.previous_customer_ticket.zone_stop || '';

            if (
                !ticket ||
                ticket.available === false ||
                ticket.is_enabled === false
            ) {
                throw new Error('Ticket unavailable');
            }

            if (
                ticket.year !== renewalTicket.year ||
                ticket.service_id !== renewalTicket.service_id
            ) {
                throw new Error();
            }

            let mandates = [];
            if (ticket && ticket.payment_type === 'dd') {
                mandates = await this.getMandates(client.id);
            }

            const service = this.extractService(client, ticket.service_id);

            let safeZone = '';
            let safeZoneStop = '';
            let safeStop = '';
            let safeDest = '';

            // Previous zone selection may no longer be valid
            if (ticket.pricing_model === 'Zonal') {
                if (!hasValidZonePricing(ticket, service)) {
                    throw new Error('Zonal pricing misconfiguration');
                }

                if (service.zones) {
                    const serviceZone = service.zones.find(
                        z => z.name === zone
                    );

                    if (serviceZone) {
                        safeZone = zone;

                        if (
                            serviceZone.stops &&
                            serviceZone.stops.length &&
                            serviceZone.stops.includes(zoneStop)
                        ) {
                            safeZoneStop = zoneStop;
                        }
                    }
                }
            } else if (stop) {
                if (
                    service.stops &&
                    service.stops.length &&
                    service.stops.find(item => item === stop)
                ) {
                    safeStop = stop;
                }
            }

            if (dest) {
                if (
                    service.destinations &&
                    service.destinations.length &&
                    service.destinations.find(item => item === dest)
                ) {
                    safeDest = dest;
                }
            }

            this.setState({
                extId,
                client,
                ticket,
                service,
                renewal,
                passenger,
                mandates,
                stop: safeStop,
                dest: safeDest,
                yr,
                zone: safeZone,
                zoneStop: safeZoneStop,
                isLoading: false,
                error: ''
            });
        } catch (e) {
            console.error(e);
            let errorMessage =
                'There was a problem retrieving the ticket. Please try again later or contact our support team.';
            if (e.message === 'Ticket unavailable') {
                errorMessage =
                    'Ticket is no longer available for purchase. Please contact your transport provider for more information.';
            }

            if (e.response && e.response.status === 403) {
                errorMessage =
                    'Permission denied: Renewals can only be accessed from the account which purchased the original ticket. Hint: Please log in with the email address that received the renewal notification.';
            }

            if (
                e.response &&
                e.response.status === 503 &&
                e.response.data.error &&
                e.response.data.error.key === 'gocardless.offline'
            ) {
                errorMessage = e.response.data.error.message;
            }

            this.setState({
                isLoading: false,
                error: errorMessage
            });
        }
    }

    getMandates = async clientId => {
        const currentSession = await Auth.currentSession();

        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        const response = await axios.get(
            `${process.env.REACT_APP_PAYMENT_API_ENDPOINT}/customer-mandate/client/${clientId}`,
            {
                headers
            }
        );

        return response.data;
    };

    formatRemainingTickets = ticket => {
        const ticketWord = ticket.remaining > 1 ? 'tickets' : 'ticket';
        return `Only ${ticket.remaining} ${ticketWord} left, hurry!`;
    };

    extractService(client, serviceId) {
        try {
            const { services = [] } = client;

            if (!services.length) {
                throw new Error();
            }

            const service = services.find(item => item.id === serviceId);

            if (!service) {
                throw new Error('Could not find service for specified client.');
            }

            return service;
        } catch (e) {
            this.setState({
                error:
                    'There was a problem retrieving the ticket info, please try again later'
            });
            return null;
        }
    }

    render() {
        const {
            passenger,
            ticket,
            client,
            service,
            stop,
            dest,
            zone,
            zoneStop,
            error,
            renewal,
            mandates,
            isLoading,
            yr,
            extId
        } = this.state;

        const { classes } = this.props;

        if (isLoading) {
            return (
                <div className={classes.root}>
                    <CircularProgress
                        id="loading-spinner"
                        className={classes.spinner}
                    />
                </div>
            );
        }

        if (error) {
            return (
                <div className={classes.root}>
                    <Typography className={classes.error} variant="body2">
                        {error}
                    </Typography>
                </div>
            );
        }

        if (
            ticket &&
            ticket.payment_type === 'dd' &&
            mandates &&
            !mandates.length
        ) {
            return (
                <Redirect
                    to={`/create-renewal-mandate/${client.id}/${renewal.id}`}
                />
            );
        }

        let confirmPurchaseComponent;

        const disablePhoto =
            client && client.features && client.features.disable_photo === true;

        const requireStudentId =
            client &&
            client.features &&
            client.features.require_id === 'student';

        if (ticket && ticket.payment_type === 'dd') {
            confirmPurchaseComponent = (
                <ConfirmDirectDebitRenewal
                    ticket={ticket}
                    passenger={passenger}
                    stop={stop}
                    dest={dest}
                    yr={yr}
                    zone={zone}
                    zoneStop={zoneStop}
                    client={client}
                    service={service}
                    renewal={renewal}
                    disablePhoto={disablePhoto}
                    extId={extId}
                    requireStudentId={requireStudentId}
                />
            );
        } else {
            confirmPurchaseComponent = (
                <ConfirmRenewal
                    ticket={ticket}
                    passenger={passenger}
                    stop={stop}
                    dest={dest}
                    yr={yr}
                    zone={zone}
                    zoneStop={zoneStop}
                    client={client}
                    service={service}
                    renewal={renewal}
                    disablePhoto={disablePhoto}
                    extId={extId}
                    requireStudentId={requireStudentId}
                />
            );
        }

        const showAvailabilityWarning =
            'remaining' in ticket &&
            ticket.remaining > 0 &&
            ticket.remaining <= 10;

        return (
            <div className={classes.root}>
                {showAvailabilityWarning && (
                    <div className={classes.availabilityWarning}>
                        <Typography
                            className={classes.availabilityWarningHeading}
                            variant="body2"
                        >
                            {this.formatRemainingTickets(ticket)}
                        </Typography>
                        <Typography
                            className={classes.availabilityWarningText}
                            variant="body1"
                        >
                            If you need more tickets than available please do
                            not purchase this ticket.
                        </Typography>
                    </div>
                )}
                <div>{confirmPurchaseComponent}</div>
            </div>
        );
    }
}

export default withStyles(styles)(RenewTicket);
